/**
 * Checkout and subscription management.
 *
 * Both flows are redirects to Stripe. We never render a card field, so there is
 * no payment state to hold here — only the getting-there and the coming-back.
 */
import type { Tier } from "../../shared/tiers";
import { identity, refresh } from "../auth/session";
import { ApiError, request } from "./client";

export type Interval = "monthly" | "annual";
export type PaidTier = Exclude<Tier, "free">;

/** Amounts in cents, so no float ever touches a price. */
export interface PriceInfo {
  monthly: number;
  annual: number;
}

export const PRICES: Record<PaidTier, PriceInfo> = {
  creator: { monthly: 499, annual: 3900 },
  studio: { monthly: 1499, annual: 11900 },
};

export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * The annual saving, stated as arithmetic rather than a badge.
 *
 * "$39/year — $3.25/month, save $20.88" beats "SAVE 35%": a number that can be
 * checked reads as trustworthy, a percentage in a coloured pill reads as
 * marketing. See docs/decisions/pricing-and-checkout.md.
 */
export function annualSaving(tier: PaidTier): {
  perMonth: string;
  saved: string;
  percent: number;
} {
  const { monthly, annual } = PRICES[tier];
  const yearAtMonthly = monthly * 12;
  return {
    perMonth: `$${(annual / 12 / 100).toFixed(2)}`,
    saved: formatPrice(yearAtMonthly - annual),
    percent: Math.round(((yearAtMonthly - annual) / yearAtMonthly) * 100),
  };
}

/**
 * What the user is told immediately before leaving the site.
 *
 * One small panel, four lines. It costs one click and removes the entire class
 * of "what am I actually being charged?" hesitation — and it sets the
 * expectation that the domain is about to change, so Stripe's URL reads as
 * normal rather than alarming.
 */
export function checkoutSummary(
  tier: PaidTier,
  interval: Interval,
  /**
   * A first charge that differs from the recurring one — a trial, an intro
   * price, a proration. None exist yet; the parameter is what makes the rule
   * below expressible rather than merely true by accident.
   */
  firstChargeCents?: number,
) {
  const recurring = PRICES[tier][interval];
  const amount = formatPrice(recurring);
  const cadence = interval === "annual" ? "year" : "month";
  const first = firstChargeCents ?? recurring;
  return {
    // Just the plan name. The selected interval option already says "/ year",
    // and the charge line underneath says it again — a heading that repeats
    // both is the third telling of one fact.
    heading: tier === "creator" ? "Creator" : "Studio",
    /**
     * Null when it would only repeat the price already on the button.
     *
     * "$39.00 / year" on the card, then "$39.00 today, then $39.00 every
     * year" beneath it, states one number three times — and a page that keeps
     * restating an uncontested price reads as though it expects an argument.
     * The line exists to disclose a *surprise*: a first charge that is not the
     * recurring one. When there is no surprise there is nothing to disclose,
     * and the annual saving is already spelled out on the card that offers it.
     */
    charge:
      first === recurring ? null : `${formatPrice(first)} today, then ${amount} every ${cadence}`,
    // One footnote instead of two. "Cancel anytime" and "we never see your
    // card" are both reassurances and reading them as separate paragraphs
    // makes the page feel like it is protesting.
    reassurance:
      "Cancel anytime, and your designs stay yours. Payments are handled by Stripe — we never see your card.",
    action: "Continue to Stripe",
  };
}

/** Start checkout. Resolves to the URL to send the browser to. */
export async function startCheckout(tier: PaidTier, interval: Interval): Promise<string> {
  const { url } = await request<{ url: string }>("/api/stripe/checkout", {
    method: "POST",
    body: { tier, interval },
  });
  return url;
}

/** Open the Customer Portal — cancel, switch plan, invoices, card updates. */
export async function openPortal(): Promise<string> {
  const { url } = await request<{ url: string }>("/api/stripe/portal", { method: "POST" });
  return url;
}

export type ActivationState =
  | { status: "activating"; elapsedMs: number }
  | { status: "active"; tier: Tier }
  | { status: "timeout" };

/**
 * Wait for a completed checkout to actually take effect.
 *
 * **This is the race that breaks most Stripe integrations.** `success_url`
 * fires from the browser the instant Checkout completes; the webhook that
 * grants the tier arrives independently and may be seconds behind. Show
 * "You're on Creator!" on arrival and the app will contradict you — or worse,
 * show Free and let the user conclude their payment failed.
 *
 * So we poll `/api/me`, which reads the tier straight from D1 with no cache in
 * the path, and we say *activating* until it changes. Nothing claims success it
 * cannot see.
 *
 * A timeout is not a failure — the payment went through, the webhook is merely
 * late. The caller says so honestly and offers a refresh rather than an error.
 */
export async function awaitEntitlement(
  onState: (state: ActivationState) => void,
  options: {
    timeoutMs?: number;
    intervalMs?: number;
    signal?: AbortSignal;
    /** Tier before checkout. Defaults to whatever identity holds right now. */
    from?: Tier;
  } = {},
): Promise<ActivationState> {
  const { timeoutMs = 15_000, intervalMs = 1_000, signal } = options;
  const startedAt = Date.now();

  /**
   * What we are waiting to move *away from*, not "anything but free".
   *
   * A Creator upgrading to Studio is already non-free, so a `tier !== "free"`
   * test reports success on the first poll and shows them the plan they had
   * before. Comparing against the tier at entry is the only version that works
   * for both a first subscription and a plan change.
   */
  const before = options.from ?? identity().tier;

  for (;;) {
    if (signal?.aborted) return { status: "timeout" };

    const current = await refresh();
    if (current.tier !== before) {
      const done: ActivationState = { status: "active", tier: current.tier };
      onState(done);
      return done;
    }

    const elapsedMs = Date.now() - startedAt;
    if (elapsedMs >= timeoutMs) {
      const done: ActivationState = { status: "timeout" };
      onState(done);
      return done;
    }

    onState({ status: "activating", elapsedMs });
    await sleep(intervalMs, signal);
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    // Detached on both paths. The signal outlives each individual sleep, so a
    // listener left behind per poll accumulates one per second for as long as
    // the page waits.
    const done = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal?.addEventListener("abort", done, { once: true });
  });
}

/**
 * Read the `?checkout=` the return URL carries, and strip it.
 *
 * Stripping matters: leaving it in means a refresh re-enters the "activating"
 * state on a page that settled minutes ago.
 */
export function consumeCheckoutReturn(): "success" | "cancelled" | null {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const value = url.searchParams.get("checkout");
  if (value !== "success" && value !== "cancelled") return null;

  url.searchParams.delete("checkout");
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  return value;
}

export function explainBillingFailure(err: unknown): string {
  if (!(err instanceof ApiError)) return "Could not reach billing. Nothing was charged.";
  if (err.isOffline) return "You are offline. Nothing was charged.";
  if (err.status === 503) return "Billing is not set up on this site yet.";
  if (err.status === 400) return err.message;
  // The portal 400s with this when the account has never checked out.
  if (/no Stripe customer/i.test(err.message)) return "You do not have a subscription yet.";
  return "Could not reach billing. Nothing was charged.";
}
